import {
  CaInternationalAccountForm,
  formatCaInternationalFormData,
  useCaInternationalAccountForm
} from './components/CaInternationalAccountForm'
import {
  AustralianAccountForm,
  useAustralianAccountForm
} from './components/AustralianAccountForm'
import {
  CaBankAccountForm,
  useCaBankAccountForm
} from './components/CaBankAccountForm'
import {
  IbanAccountForm,
  useIBANAccountForm
} from './components/IbanAccountForm'
import {
  UkBankAccountForm,
  useUkBankAccountForm
} from './components/UkBankAccountForm'
import {
  UsAchAccountForm,
  useUsAchAccountForm
} from './components/UsAchAccountForm'
import {
  formatUsWireFormData,
  useUsWireAccountForm,
  UsWireAccountForm
} from './components/UsWireAccountForm'
import {
  SwedishBankgiroForm,
  useSwedishBankgiroForm
} from './components/SwedishBankgiroForm'
import { MerchantBankAccountDetails } from './bankDetails.types'

export const usePaymentDetailsForm = ({
  accountType,
  showValidationErrors,
  bankDetails
}: {
  accountType?: string
  showValidationErrors: boolean
  bankDetails?: MerchantBankAccountDetails['value']
}) => {
  const ibanForm = useIBANAccountForm({
    showValidationErrors,
    bankDetails
  })

  const ukBankAccountForm = useUkBankAccountForm({
    showValidationErrors,
    bankDetails
  })

  const australianAccountForm = useAustralianAccountForm({
    showValidationErrors,
    bankDetails
  })

  const usAchAccountForm = useUsAchAccountForm({
    showValidationErrors,
    bankDetails
  })

  const caBankAccountForm = useCaBankAccountForm({
    showValidationErrors,
    bankDetails
  })

  const usWireAccountForm = useUsWireAccountForm({
    showValidationErrors,
    bankDetails
  })

  const caInternationalAccountForm = useCaInternationalAccountForm({
    showValidationErrors,
    bankDetails
  })

  const swedishBankgiroForm = useSwedishBankgiroForm({
    showValidationErrors,
    bankDetails
  })

  const accountFormMap = {
    IBAN: {
      queries: ibanForm.queries,
      component: <IbanAccountForm fields={ibanForm.fields} />,
      value: {
        type: 'IBAN' as const,
        iban: ibanForm.queries.formData.iban
      }
    },
    UK_BANK_ACCOUNT: {
      queries: ukBankAccountForm.queries,
      component: <UkBankAccountForm fields={ukBankAccountForm.fields} />,
      value: {
        type: 'UK_BANK_ACCOUNT' as const,
        ukBankAccount: ukBankAccountForm.queries.formData
      }
    },
    AUSTRALIAN: {
      queries: australianAccountForm.queries,
      component: (
        <AustralianAccountForm fields={australianAccountForm.fields} />
      ),
      value: {
        type: 'AUSTRALIAN' as const,
        australianDestination: australianAccountForm.queries.formData
      }
    },
    CA_BANK_ACCOUNT: {
      queries: caBankAccountForm.queries,
      component: <CaBankAccountForm fields={caBankAccountForm.fields} />,
      value: {
        type: 'CA_BANK_ACCOUNT' as const,
        canadianBankAccount: caBankAccountForm.queries.formData
      }
    },
    US_ACH: {
      queries: usAchAccountForm.queries,
      component: <UsAchAccountForm fields={usAchAccountForm.fields} />,
      value: {
        type: 'US_ACH' as const,
        usAchDestination: usAchAccountForm.queries.formData
      }
    },
    US_WIRE: {
      queries: usWireAccountForm.queries,
      component: <UsWireAccountForm fields={usWireAccountForm.fields} />,
      value: {
        type: 'US_WIRE' as const,
        usWireDestination: formatUsWireFormData(
          usWireAccountForm.queries.formData
        )
      }
    },

    CA_INTERNATIONAL: {
      queries: caInternationalAccountForm.queries,
      component: (
        <CaInternationalAccountForm
          fields={caInternationalAccountForm.fields}
        />
      ),
      value: {
        type: 'CA_INTERNATIONAL' as const,
        canadianInternationalDestination: formatCaInternationalFormData(
          caInternationalAccountForm.queries.formData
        )
      }
    },
    SWEDISH_BANKGIRO: {
      queries: swedishBankgiroForm.queries,
      component: <SwedishBankgiroForm fields={swedishBankgiroForm.fields} />,
      value: {
        type: 'SWEDISH_BANKGIRO' as const,
        swedishBankgiroDestination: swedishBankgiroForm.queries.formData
      }
    }
  }

  const data = accountType
    ? accountFormMap[accountType as keyof typeof accountFormMap]
    : null

  return data
}
